import {Hemisphere, PlantGroup, PlantSummary} from '../types';
import {kindInfo} from './kinds';
import {seasonKey} from './season';
import {formatDayMonth} from './when';

// The Plants list's one-line subtitles and its search, from the summary the API gives per plant.

/** "Planted out 17 Oct · Back bed · 4 tells" / "Back bed · since 2019" / "2022/23 · 11 tells · looks permanent". */
export function plantSubtitle(plant: PlantSummary, hemisphere: Hemisphere | null | undefined): string {
    const tells = (n: number) => `${n} ${n === 1 ? 'tell' : 'tells'}`;
    const parts: (string | null)[] =
        plant.group === 'growing'
            ? [plant.latest ? `${kindInfo(plant.latest.kind).label} ${formatDayMonth(plant.latest.whenLocal)}` : null, plant.latest?.placeName ?? null, plant.seasonTells > 0 ? tells(plant.seasonTells) : null]
            : plant.group === 'permanent'
              ? [plant.latest?.placeName ?? null, plant.firstAt ? `since ${plant.firstAt.slice(0, 4)}` : null]
              : [plant.lastAt ? seasonKey(plant.lastAt, hemisphere) : null, plant.tellCount > 0 ? tells(plant.tellCount) : null, plant.looksPermanent ? 'looks permanent' : null];
    return parts.filter(Boolean).join(' · ');
}

export const matchesPlant = (plant: PlantSummary, q: string): boolean => plant.label.toLowerCase().includes(q.trim().toLowerCase());

// ------------------------------------------------------------------ species

/** One plant name with all its varieties ("Garlic" · 11). A view; nothing is stored for it. */
export interface Species {
    /** Lower-cased name, the species page's address and the feed's `species` filter. */
    key: string;
    /** As most often written. */
    name: string;
    plants: PlantSummary[];
    /** The first band any variety qualifies for: growing, then permanent, then past. */
    group: PlantGroup;
    /** The variety the row speaks for: the most recent one in that band. */
    lead: PlantSummary;
    thumbUrl: string | null;
    tellCount: number;
    lastAt: string | null;
}

export const speciesKeyOf = (name: string): string => name.toLowerCase().replace(/\s+/g, ' ').trim();

const recency = (p: PlantSummary): string => p.latest?.whenLocal ?? p.lastAt ?? '';
const GROUP_ORDER: PlantGroup[] = ['growing', 'permanent', 'past'];

/** Folds the Plants list into species, keeping the server's order of first appearance. */
export function groupSpecies(plants: PlantSummary[]): Species[] {
    const byKey = new Map<string, PlantSummary[]>();
    for (const plant of plants) {
        const key = speciesKeyOf(plant.name);
        byKey.set(key, [...(byKey.get(key) ?? []), plant]);
    }
    return [...byKey.entries()].map(([key, members]) => {
        const group = GROUP_ORDER.find(g => members.some(p => p.group === g)) ?? 'past';
        const newestFirst = [...members].sort((a, b) => (recency(a) < recency(b) ? 1 : recency(a) > recency(b) ? -1 : 0));
        const lead = newestFirst.find(p => p.group === group) ?? newestFirst[0];
        const lastAt = members.reduce<string | null>((m, p) => (p.lastAt && (m === null || p.lastAt > m) ? p.lastAt : m), null);
        return {
            key,
            name: [...members].sort((a, b) => b.useCount - a.useCount)[0].name,
            plants: members,
            group,
            lead,
            thumbUrl: newestFirst.find(p => p.thumbUrl)?.thumbUrl ?? null,
            tellCount: members.reduce((n, p) => n + p.tellCount, 0),
            lastAt
        };
    });
}

/** "11 varieties" counts the plain name too: it is one of the plants the gardener wrote down. */
export const varietiesLabel = (n: number): string => `${n} ${n === 1 ? 'variety' : 'varieties'}`;
